import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Heading,
  Text,
  useToast,
  Divider,
  HStack,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useQuery, useMutation } from "react-query";
import { getSettings, updateSettings } from "../services/settingService.js";
import Card from "../components/Card/Card.js";
import CardBody from "../components/Card/CardBody.js";
import CardHeader from "../components/Card/CardHeader.js";

export default function SiteSettings() {
  const toast = useToast();
  const [formData, setFormData] = useState({
    storeName: "",
    aboutUs: "",
    logo: "",
    address: "",
    phone: "",
    enamad: "",
    headerMenus: [{ title: "", link: "" }], 
    footerMenus: [{ title: "", link: "" }], 
  });
  const [logoReading, setLogoReading] = useState(false);
  const formInitialized = React.useRef(false);

  const { isLoading } = useQuery('siteSettings', getSettings, {
    onSuccess: (data) => {
      // فقط یک‌بار فرم را از سرور پر کن؛ refetch نباید لوگو/ویرایش‌های کاربر را پاک کند
      if (data && !formInitialized.current) {
        formInitialized.current = true;
        setFormData({
          storeName: data.storeName || "",
          aboutUs: data.aboutUs || "",
          logo: data.logo || "",
          address: data.address || "",
          phone: data.phone || "",
          enamad: data.enamad || "",
          headerMenus: data.headerMenus?.length ? data.headerMenus : [{ title: "", link: "" }],
          footerMenus: data.footerMenus?.length ? data.footerMenus : [{ title: "", link: "" }],
        });
      }
    }
  });

  const mutation = useMutation(updateSettings, {
    onSuccess: () => {
      toast({
        title: "تنظیمات ذخیره شد.",
        description: "تنظیمات سایت با موفقیت به‌روزرسانی شدند.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "خطا در ذخیره",
        description: error?.response?.data?.message || "خطایی رخ داد.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setLogoReading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      // functional update تا state قدیمی (stale closure) لوگو را نبلعد
      setFormData((prev) => ({ ...prev, [field]: reader.result || "" }));
      setLogoReading(false);
    };
    reader.onerror = () => {
      setLogoReading(false);
      toast({
        title: "خطا در خواندن فایل",
        description: "لطفاً دوباره تصویر لوگو را انتخاب کنید.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleMenuChange = (index, type, field, value) => {
    setFormData((prev) => {
      const newMenus = [...prev[type]];
      newMenus[index] = { ...newMenus[index], [field]: value };
      return { ...prev, [type]: newMenus };
    });
  };

  const addMenuItem = (type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: [...prev[type], { title: "", link: "" }],
    }));
  };

  const removeMenuItem = (index, type) => {
    setFormData((prev) => {
      const newMenus = [...prev[type]];
      newMenus.splice(index, 1);
      return { ...prev, [type]: newMenus };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (logoReading) {
      toast({
        title: "لطفاً صبر کنید",
        description: "در حال آماده‌سازی لوگو برای ارسال...",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    mutation.mutate({
      storeName: formData.storeName,
      aboutUs: formData.aboutUs,
      logo: formData.logo,
      address: formData.address,
      phone: formData.phone,
      enamad: formData.enamad,
      headerMenus: formData.headerMenus.filter(m => m.title),
      footerMenus: formData.footerMenus.filter(m => m.title),
    });
  };

  const renderMenuBuilder = (type, label) => (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <FormLabel mb={0}>{label}</FormLabel>
        <Button size="sm" leftIcon={<AddIcon />} colorScheme="teal" onClick={() => addMenuItem(type)}>
          افزودن منو
        </Button>
      </Flex>
      <VStack align="stretch" spacing={3}>
        {formData[type].map((item, index) => (
          <HStack key={index} align="flex-end">
            <FormControl>
              <FormLabel fontSize="xs">عنوان</FormLabel>
              <Input
                placeholder="مثال: درباره ما"
                value={item.title}
                onChange={(e) => handleMenuChange(index, type, "title", e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">لینک</FormLabel>
              <Input
                dir="ltr"
                placeholder="مثال: /about"
                value={item.link}
                onChange={(e) => handleMenuChange(index, type, "link", e.target.value)}
              />
            </FormControl>
            <IconButton
              aria-label="حذف"
              icon={<DeleteIcon />}
              colorScheme="red"
              onClick={() => removeMenuItem(index, type)}
            />
          </HStack>
        ))}
      </VStack>
    </Box>
  );

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px">
          <Flex direction="column">
            <Heading as="h3" size="md" mb="4px">
              تنظیمات سایت
            </Heading>
            <Text color="gray.400" fontSize="sm">
              مدیریت اطلاعات فروشگاه، منوها و اینماد
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box as="form" onSubmit={handleSubmit} w="100%">
            <VStack spacing={6} align="stretch">
              <FormControl id="storeName">
                <FormLabel>نام فروشگاه</FormLabel>
                <Input
                  name="storeName"
                  value={formData.storeName}
                  onChange={handleChange}
                  placeholder="مثال: فروشگاه من"
                />
              </FormControl>

              <FormControl id="aboutUs">
                <FormLabel>درباره ما (متن کوتاه)</FormLabel>
                <Textarea
                  name="aboutUs"
                  value={formData.aboutUs}
                  onChange={handleChange}
                  placeholder="فروشگاه ما بهترین محصولات را ارائه می‌دهد..."
                  rows={3}
                />
              </FormControl>

              <FormControl id="logo">
                <FormLabel>لوگو</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'logo')}
                  mb={2}
                />
                {formData.logo && (
                  <Image src={formData.logo} alt="Logo Preview" maxH="100px" objectFit="contain" />
                )}
              </FormControl>

              <FormControl id="phone">
                <FormLabel>تلفن</FormLabel>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="021-12345678"
                  dir="ltr"
                />
              </FormControl>

              <FormControl id="address">
                <FormLabel>آدرس</FormLabel>
                <Textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="تهران، خیابان..."
                />
              </FormControl>

              <FormControl id="enamad">
                <FormLabel>کد اینماد (HTML/Script)</FormLabel>
                <Textarea
                  name="enamad"
                  value={formData.enamad}
                  onChange={handleChange}
                  placeholder="<script src='...'></script>"
                  dir="ltr"
                />
              </FormControl>

              <Divider />

              <Heading as="h4" size="sm">تنظیمات منو</Heading>

              {renderMenuBuilder("headerMenus", "منوی هدر")}
              
              {renderMenuBuilder("footerMenus", "منوی فوتر")}

              <Button
                colorScheme="blue"
                type="submit"
                alignSelf="flex-end"
                isLoading={mutation.isLoading || logoReading}
                loadingText={logoReading ? "آماده‌سازی لوگو..." : "در حال ذخیره..."}
              >
                ذخیره تنظیمات
              </Button>
            </VStack>
          </Box>
        </CardBody>
      </Card>
    </Flex>
  );
}
